import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActorType } from '@models/api-calls/application/application.model';
import {
    AllSubSteps,
    ApplicationInformationSubSteps,
    MainSteps,
    PaymentSubSteps,
    PersonalDetailsSubSteps,
    ProductSelectionSubSteps,
    SummarySubSteps,
} from '@models/config/app-structure.model';
import { AddressFormControls } from '@models/form/form.model';
import { ApplicationDataService } from '@services/api-calls/application-data.service';
import { ApplicationProductsService } from '@services/api-calls/application-products.service';
import { ApplicationService } from '@services/api-calls/application.service';
import { AddressValidationService } from '@services/api-calls/address-validation.service';
import { BeneficiaryService } from '@services/api-calls/beneficiary.service';
import { ExistingInsuranceService } from '@services/api-calls/existing-insurance.service';
import { GiactService } from '@services/api-calls/giact.service';
import { InsuredClientService } from '@services/api-calls/insured-client.service';
import { PaymentDataService } from '@services/api-calls/payment-data.service';
import { PaymentFormPopulationService } from '@services/api-calls/payment-form-population.service';
import { PaymentSaveService } from '@services/api-calls/payment-save.service';
import { PaymentService } from '@services/api-calls/payment.service';
import { FormRegistryService } from '@services/form/form-registry.service';
import {
    AchDisclaimerFormEnum,
    ExistingInsuranceSubstepFormEnum,
    GiactOutcomeEnum,
    GiactStatusEnum,
    IdentityVerificationConsentFormEnum,
    MailingAddressInformationFormEnum,
    OwnerSubstepFormEnum,
    PaymentInformationFormEnum,
    ProductSelectionFormEnum,
    ResidentialInformationFormEnum,
    YesNoOptionsEnum,
} from 'app/enums/form/form.enum';
import { AddressType } from 'app/enums/form/address.enum';
import { catchError, concat, delay, last, Observable, of, switchMap } from 'rxjs';
import { FormGroup } from '@angular/forms';

@Injectable({
    providedIn: 'root',
})
export class SubstepSaveFunctionService {
    constructor(
        private insuredClientService: InsuredClientService,
        private beneficiaryService: BeneficiaryService,
        private existingInsuranceService: ExistingInsuranceService,
        private applicationDataService: ApplicationDataService,
        private giactService: GiactService,
        private applicationService: ApplicationService,
        private formRegistry: FormRegistryService,
        private paymentSaveService: PaymentSaveService,
        private paymentFormPopulationService: PaymentFormPopulationService,
        private paymentDataService: PaymentDataService,
        private applicationProductsService: ApplicationProductsService,
        private addressValidationService: AddressValidationService,
        private translateService: TranslateService,
        private paymentService: PaymentService,
    ) {}

    hasChanges(mainStep: MainSteps, subStep: AllSubSteps): boolean {
        const key = `${mainStep}:${subStep}`;

        switch (key) {
            case `${MainSteps.PERSONAL_DETAILS}:${PersonalDetailsSubSteps.PERSONAL_INFORMATION}`:
            case `${MainSteps.PERSONAL_DETAILS}:${PersonalDetailsSubSteps.RESIDENCY_AND_IDENTIFICATION}`:
            case `${MainSteps.PERSONAL_DETAILS}:${PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION}`:
            case `${MainSteps.PERSONAL_DETAILS}:${PersonalDetailsSubSteps.MAILING_ADDRESS}`:
                return this.insuredClientService.hasChangesComparedToServer();

            // CONTACT_INFORMATION persists client fields (email/phone) AND the
            // e-delivery opt-in (app_emc).
            case `${MainSteps.PERSONAL_DETAILS}:${PersonalDetailsSubSteps.CONTACT_INFORMATION}`:
                return this.insuredClientService.hasChangesComparedToServer() || this.applicationDataService.hasChanges();

            case `${MainSteps.PRODUCT_SELECTION}:${ProductSelectionSubSteps.PRODUCT_SELECTION}`: {
                const selectedProductId = this.formRegistry.getControlValue<number>([
                    MainSteps.PRODUCT_SELECTION,
                    ProductSelectionSubSteps.PRODUCT_SELECTION,
                    ProductSelectionFormEnum.SELECTED_PRODUCT_ID,
                ])?.();
                const coverage = this.formRegistry.getControlValue<number>([
                    MainSteps.PRODUCT_SELECTION,
                    ProductSelectionSubSteps.PRODUCT_SELECTION,
                    ProductSelectionFormEnum.COVERAGE,
                ])?.();

                if (!selectedProductId || !coverage) return false;

                const savedProducts = this.applicationService.rawApplication()?.app_products;
                if (!savedProducts) return true;

                const saved = Array.isArray(savedProducts) ? savedProducts[0] : savedProducts;
                return !saved || saved.coverage !== coverage || saved.product_id !== selectedProductId;
            }

            case `${MainSteps.APPLICATION_INFORMATION}:${ApplicationInformationSubSteps.OWNER}`: {
                const formValue = this.formRegistry.getControlValue<number>([
                    MainSteps.APPLICATION_INFORMATION,
                    ApplicationInformationSubSteps.OWNER,
                    OwnerSubstepFormEnum.IS_CLIENT_OWNER,
                ])?.();

                const rawApp = this.applicationService.rawApplication();
                if (!rawApp || formValue == null) return false;

                const serverIsOwner =
                    rawApp.owner_client_id != null && rawApp.insured_client_id != null && rawApp.owner_client_id === rawApp.insured_client_id
                        ? YesNoOptionsEnum.YES
                        : YesNoOptionsEnum.NO;

                return formValue !== serverIsOwner;
            }

            case `${MainSteps.APPLICATION_INFORMATION}:${ApplicationInformationSubSteps.BENEFICIARY}`:
                return this.beneficiaryService.hasChangesComparedToServer();

            case `${MainSteps.APPLICATION_INFORMATION}:${ApplicationInformationSubSteps.EXISTING_INSURANCE}`:
                return this.applicationDataService.hasChanges();

            case `${MainSteps.APPLICATION_INFORMATION}:${ApplicationInformationSubSteps.EXISTING_INSURANCE_REPLACEMENT}`:
                return this.existingInsuranceService.hasChangesComparedToServer() || this.applicationDataService.hasChanges();

            case `${MainSteps.APPLICATION_INFORMATION}:${ApplicationInformationSubSteps.STATEMENTS_AND_AGREEMENTS}`:
                return this.applicationDataService.hasChanges();

            case `${MainSteps.PAYMENT}:${PaymentSubSteps.PAYMENT_INFORMATION}`:
                return this.hasPaymentInfoChanges();

            case `${MainSteps.PAYMENT}:${PaymentSubSteps.ACH_DISCLAIMER}`:
                return this.hasAchDisclaimerChanges();

            // FINAL_CONSENTS surfaces statements + fraud (app_emc) + ACH (app_payment).
            // Save runs if either side has dirty changes.
            case `${MainSteps.SUBMISSION}:${PaymentSubSteps.FINAL_CONSENTS}`:
                return this.applicationDataService.hasChanges() || this.hasAchDisclaimerChanges();

            default:
                return false;
        }
    }

    private hasPaymentInfoChanges(): boolean {
        const server = this.paymentDataService.applicationPayment();
        if (!server) return false;

        const frequency = this.formRegistry.getControlValue<string | null>([
            MainSteps.PAYMENT,
            PaymentSubSteps.PAYMENT_INFORMATION,
            PaymentInformationFormEnum.PAYMENT_FREQUENCY,
        ])?.();
        const paymentDate = this.formRegistry.getControlValue<string | null>([
            MainSteps.PAYMENT,
            PaymentSubSteps.PAYMENT_INFORMATION,
            PaymentInformationFormEnum.PAYMENT_DATE,
        ])?.();

        return frequency !== server.payment_frequency || paymentDate !== server.payment_date_options;
    }

    private hasAchDisclaimerChanges(): boolean {
        const server = this.paymentDataService.applicationPayment();
        const formValue = this.formRegistry.getControlValue<number | null>([
            MainSteps.PAYMENT,
            PaymentSubSteps.ACH_DISCLAIMER,
            AchDisclaimerFormEnum.ACH_PAYMENT_DISCLAIMER_ACCEPTED,
        ])?.();

        return (formValue ?? null) !== (server?.has_payment_disclosures ?? null);
    }

    callSaveFunction(mainStep: MainSteps, subStep: AllSubSteps): Observable<any> | undefined {
        const key = `${mainStep}:${subStep}`;

        switch (key) {
            case `${MainSteps.PERSONAL_DETAILS}:${PersonalDetailsSubSteps.PERSONAL_INFORMATION}`:
            // IAPP2-1741: Finance Information substep removed (all fields removed)
            // case `${MainSteps.PERSONAL_DETAILS}:${PersonalDetailsSubSteps.FINANCE_INFORMATION}`:
                return this.insuredClientService.createOrUpdateInsuredClient();

            // CONTACT_INFORMATION: persist client fields (email/phone) AND the
            // e-delivery opt-in (app_emc).
            case `${MainSteps.PERSONAL_DETAILS}:${PersonalDetailsSubSteps.CONTACT_INFORMATION}`: {
                const clientSave$ = this.insuredClientService.createOrUpdateInsuredClient();
                const emcSave$ = this.applicationDataService.updateApplication();

                // Sequential, not forkJoin: clientSave$ PATCHes clients/{id} and
                // emcSave$ PATCHes applications/{id} — same application aggregate.
                // Concurrent writes deadlock InnoDB.
                if (clientSave$ && emcSave$) {
                    return concat(clientSave$, emcSave$).pipe(last());
                }
                return clientSave$ ?? emcSave$;
            }

            case `${MainSteps.PERSONAL_DETAILS}:${PersonalDetailsSubSteps.RESIDENCY_AND_IDENTIFICATION}`:
                return this.insuredClientService.createOrUpdateInsuredClient();

            case `${MainSteps.PERSONAL_DETAILS}:${PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION}`:
                return this.validateAndSaveAddress(
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION,
                    ResidentialInformationFormEnum.ADDRESS_GROUP,
                    AddressType.INSURED_CLIENT_RESIDENTIAL,
                );

            case `${MainSteps.PERSONAL_DETAILS}:${PersonalDetailsSubSteps.MAILING_ADDRESS}`:
                return this.validateAndSaveAddress(
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.MAILING_ADDRESS,
                    MailingAddressInformationFormEnum.ADDRESS_GROUP,
                    AddressType.INSURED_CLIENT_MAILING,
                );

            case `${MainSteps.PERSONAL_DETAILS}:${PersonalDetailsSubSteps.IDENTITY_VERIFICATION_CONSENT}`: {
                const saveObservable = this.insuredClientService.createOrUpdateInsuredClient();
                if (!saveObservable) return undefined;

                const giactFlow$ = saveObservable.pipe(
                    switchMap((clientResponse) => {
                        const giactDisclosure = this.formRegistry.getControlValue([
                            MainSteps.PERSONAL_DETAILS,
                            PersonalDetailsSubSteps.IDENTITY_VERIFICATION_CONSENT,
                            IdentityVerificationConsentFormEnum.GIACT_DISCLOSURE,
                        ])?.();
                        const giactStatus = clientResponse?.giact_status;

                        if (giactDisclosure === 1 && giactStatus === GiactStatusEnum.SUCCESS) {
                            return of(clientResponse).pipe(delay(4000));
                        }

                        if (giactDisclosure === 1 && giactStatus && giactStatus !== GiactStatusEnum.SUCCESS) {
                            return of(clientResponse).pipe(delay(3000));
                        }

                        if (giactDisclosure === 1 && !giactStatus) {
                            const appId = this.applicationService.application()?.id;
                            if (!appId) return of(clientResponse);

                            return this.giactService.verifyIdentity(appId).pipe(
                                switchMap((giactResponse) => {
                                    const updateResult = this.insuredClientService.updateInsuredClientWithGiactResult(giactResponse);
                                    return updateResult ? updateResult.pipe(delay(3000)) : of(clientResponse).pipe(delay(3000));
                                }),
                                catchError(() => {
                                    const errorResponse = { status: GiactStatusEnum.FAILED, outcome: GiactOutcomeEnum.VERIFICATION_FAILED };
                                    const updateResult = this.insuredClientService.updateInsuredClientWithGiactResult(errorResponse);
                                    return updateResult ? updateResult.pipe(delay(3000)) : of(clientResponse).pipe(delay(3000));
                                }),
                            );
                        }
                        return of(clientResponse);
                    }),
                );

                const medicalConsent = this.formRegistry.getControlValue<number>([
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.IDENTITY_VERIFICATION_CONSENT,
                    IdentityVerificationConsentFormEnum.MEDICAL_CONSENT,
                ])?.();
                const medicalConsentSave$ = medicalConsent != null
                    ? this.applicationDataService.updateMedicalConsent(medicalConsent, { silent: true })
                    : undefined;

                // Run sequentially (NOT forkJoin): giactFlow$ PATCHes clients/{id} and
                // medicalConsentSave$ PATCHes applications/{id} — both lock the same
                // application aggregate. Firing concurrently deadlocks InnoDB
                // (ER_LOCK_DEADLOCK). concat serializes them, releasing locks between.
                return medicalConsentSave$ ? concat(giactFlow$, medicalConsentSave$).pipe(last()) : giactFlow$;
            }

            case `${MainSteps.PRODUCT_SELECTION}:${ProductSelectionSubSteps.PRODUCT_SELECTION}`: {
                const selectedProductIdSignal = this.formRegistry.getControlValue<number>([
                    MainSteps.PRODUCT_SELECTION,
                    ProductSelectionSubSteps.PRODUCT_SELECTION,
                    ProductSelectionFormEnum.SELECTED_PRODUCT_ID,
                ]);
                const selectedProductId = selectedProductIdSignal?.();

                const coverageSignal = this.formRegistry.getControlValue<number>([
                    MainSteps.PRODUCT_SELECTION,
                    ProductSelectionSubSteps.PRODUCT_SELECTION,
                    ProductSelectionFormEnum.COVERAGE,
                ]);
                const coverage = coverageSignal?.();

                if (!selectedProductId || !coverage) {
                    return undefined;
                }

                const rawApplication = this.applicationService.rawApplication();
                const savedProducts = rawApplication?.app_products;

                if (savedProducts) {
                    const saved = Array.isArray(savedProducts) ? savedProducts[0] : savedProducts;
                    if (saved && saved.coverage === coverage && saved.product_id === selectedProductId) {
                        return undefined;
                    }
                }

                return this.applicationProductsService.patchApplicationProduct(coverage, selectedProductId);
            }

            case `${MainSteps.APPLICATION_INFORMATION}:${ApplicationInformationSubSteps.OWNER}`: {
                const isClientOwner = this.formRegistry.getControlValue<number>([
                    MainSteps.APPLICATION_INFORMATION,
                    ApplicationInformationSubSteps.OWNER,
                    OwnerSubstepFormEnum.IS_CLIENT_OWNER,
                ])?.();

                if (isClientOwner === 1) {
                    const applicationId = this.applicationService.applicationId();
                    const insuredClientId = this.applicationService.application()?.insured_client_id;

                    if (applicationId && insuredClientId) {
                        return this.paymentService.addClientRole(Number(applicationId), insuredClientId, ActorType.OWNER);
                    }
                }

                return undefined;
            }

            case `${MainSteps.APPLICATION_INFORMATION}:${ApplicationInformationSubSteps.BENEFICIARY}`:
                return this.beneficiaryService.saveAllBeneficiaries();

            case `${MainSteps.APPLICATION_INFORMATION}:${ApplicationInformationSubSteps.EXISTING_INSURANCE}`: {
                const value = this.formRegistry.getSafeControlValue<number>([
                    MainSteps.APPLICATION_INFORMATION,
                    ApplicationInformationSubSteps.EXISTING_INSURANCE,
                    ExistingInsuranceSubstepFormEnum.HAS_EXISTING_INSURANCES,
                ]);
                if (value == null) return this.applicationDataService.updateApplication();
                return this.applicationDataService.updateHasExistingInsurances(value);
            }

            case `${MainSteps.APPLICATION_INFORMATION}:${ApplicationInformationSubSteps.EXISTING_INSURANCE_REPLACEMENT}`: {
                const emcSave$ = this.applicationDataService.updateApplication();
                const insurancesSave$ = this.shouldDeleteInsuranceCompanies()
                    ? this.existingInsuranceService.deleteAllInsuranceCompanies()
                    : this.existingInsuranceService.saveAllInsuranceCompanies();

                // Sequential, not forkJoin: emcSave$ PATCHes applications/{id} and
                // insurancesSave$ PUTs applications/{id}/insurances — same application
                // aggregate. Concurrent writes deadlock InnoDB.
                if (emcSave$ && insurancesSave$) {
                    return concat(emcSave$, insurancesSave$).pipe(last());
                }
                return emcSave$ ?? insurancesSave$;
            }

            case `${MainSteps.APPLICATION_INFORMATION}:${ApplicationInformationSubSteps.STATEMENTS_AND_AGREEMENTS}`:
                return this.applicationDataService.updateApplication();

            case `${MainSteps.SUMMARY}:${SummarySubSteps.SUMMARY}`:
                return undefined;

            case `${MainSteps.PAYMENT}:${PaymentSubSteps.PAYMENT_INFORMATION}`:
                return this.paymentSaveService.savePaymentInformation();

            case `${MainSteps.PAYMENT}:${PaymentSubSteps.PRIMARY_PAYMENT_METHOD}`:
            case `${MainSteps.PAYMENT}:${PaymentSubSteps.SECONDARY_PAYMENT_METHOD}`:
            case `${MainSteps.SUBMISSION}:${PaymentSubSteps.SUBMIT}`:
                return undefined;

            case `${MainSteps.PAYMENT}:${PaymentSubSteps.ACH_DISCLAIMER}`:
                return this.saveAchDisclaimer();

            // FINAL_CONSENTS: persist app_emc (statements + fraud) AND app_payment (ACH).
            case `${MainSteps.SUBMISSION}:${PaymentSubSteps.FINAL_CONSENTS}`: {
                const emcSave$ = this.applicationDataService.updateApplication();
                const paymentSave$ = this.saveAchDisclaimer();
                // Sequential, not forkJoin: emcSave$ PATCHes applications/{id} and
                // paymentSave$ PATCHes applications/{id}/payment — same application
                // aggregate. Concurrent writes deadlock InnoDB.
                if (emcSave$ && paymentSave$) {
                    return concat(emcSave$, paymentSave$).pipe(last());
                }
                return emcSave$ ?? paymentSave$;
            }

            default:
                return undefined;
        }
    }

    /** ACH_DISCLAIMER and FINAL_CONSENTS only own has_payment_disclosures. They must not rebuild the
     *  full payment payload: the payment date controls live on the Payment Information substep and are
     *  absent from the form whenever that substep isn't mounted, so a full save from here would PATCH
     *  the stored dates back to null. */
    private saveAchDisclaimer(): Observable<unknown> | undefined {
        const value = this.formRegistry.getSafeControlValue<number>([
            MainSteps.PAYMENT,
            PaymentSubSteps.ACH_DISCLAIMER,
            AchDisclaimerFormEnum.ACH_PAYMENT_DISCLAIMER_ACCEPTED,
        ]);
        if (value == null) return undefined;

        return this.paymentSaveService.updateHasPaymentDisclosures(value, { silent: true });
    }

    private shouldDeleteInsuranceCompanies(): boolean {
        const hasExisting = this.formRegistry.getSafeControlValue<number>([
            MainSteps.APPLICATION_INFORMATION,
            ApplicationInformationSubSteps.EXISTING_INSURANCE,
            ExistingInsuranceSubstepFormEnum.HAS_EXISTING_INSURANCES,
        ]);
        const applied = this.formRegistry.getSafeControlValue<number>([
            MainSteps.APPLICATION_INFORMATION,
            ApplicationInformationSubSteps.EXISTING_INSURANCE_REPLACEMENT,
            ExistingInsuranceSubstepFormEnum.EXISTING_INSURANCES_APPLIED,
        ]);

        return hasExisting === YesNoOptionsEnum.NO || (hasExisting === YesNoOptionsEnum.YES && applied === YesNoOptionsEnum.YES);
    }

    private validateAndSaveAddress(
        mainStep: MainSteps,
        subStep: PersonalDetailsSubSteps,
        addressGroupKey: string,
        addressType: AddressType,
    ): Observable<any> | undefined {
        const formGroup = this.formRegistry.getFormGroup(mainStep, subStep);
        const addressGroup = formGroup?.get(addressGroupKey) as FormGroup<AddressFormControls> | null;

        if (!addressGroup) {
            return this.insuredClientService.createOrUpdateInsuredClient();
        }

        // Skip re-validation if already validated — proceed to save regardless of dpvCode
        const existingState = this.addressValidationService.getValidationState(addressType)();
        if (existingState.dpvData !== null) {
            const saveObservable = this.insuredClientService.createOrUpdateInsuredClient();
            return saveObservable ?? of(null);
        }

        return this.addressValidationService.validateFormGroup(addressGroup, addressType).pipe(
            switchMap(() => {
                const saveObservable = this.insuredClientService.createOrUpdateInsuredClient();
                return saveObservable ?? of(null);
            }),
        );
    }
}
